import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Share2, Download, Copy, Loader2,
  MessageCircle, Twitter, Facebook, Link2, Mail, Palette,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { generateTripCard, CARD_THEMES } from './ShareCardRenderer';

const ThemeSelector = ({ activeTheme, onSelect }) => (
  <div className="flex items-center gap-2" data-testid="theme-selector">
    <Palette className="w-4 h-4 text-ocean/50 flex-shrink-0" />
    <div className="flex gap-2">
      {Object.values(CARD_THEMES).map((t) => (
        <button
          key={t.id}
          data-testid={`theme-${t.id}`}
          onClick={() => onSelect(t.id)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border-2 ${
            activeTheme === t.id
              ? 'border-ocean shadow-md scale-105'
              : 'border-transparent hover:border-ocean/20 opacity-70 hover:opacity-100'
          }`}
          style={{ background: t.swatch[0], color: t.swatch[1] }}
        >
          <span className="w-2.5 h-2.5 rounded-full border border-white/30" style={{ background: t.swatch[1] }} />
          {t.name}
        </button>
      ))}
    </div>
  </div>
);

export const SocialShareDialog = ({ open, onOpenChange, itineraryId, tripInput, generatedItinerary, onEmailShare }) => {
  const [shareImage, setShareImage] = useState(null);
  const [shareText, setShareText] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTheme, setActiveTheme] = useState('ocean');
  const [photoIndex, setPhotoIndex] = useState(0);
  const photosRef = useRef(null);

  const city = tripInput?.destination_cities?.length > 1
    ? tripInput.destination_cities.join(', ')
    : tripInput?.destination_city || 'Trip';
  const country = tripInput?.destination_country || '';
  const daysCount = generatedItinerary?.days?.length || 0;
  const fallbackText = `Check out my ${daysCount}-day trip to ${city}, ${country}! Planned with Triple A Travel Solutions. Book yours at tripleabooking.com`;

  const fetchPhotos = useCallback(async () => {
    if (photosRef.current !== null) return photosRef.current;
    const API = process.env.REACT_APP_BACKEND_URL;
    photosRef.current = [];
    try {
      const ctrl = new AbortController();
      const tid = setTimeout(() => ctrl.abort(), 12000);
      const searchCity = tripInput?.destination_cities?.[0] || tripInput?.destination_city || city;
      const resp = await fetch(`${API}/api/destination-photos?city=${encodeURIComponent(searchCity)}&country=${encodeURIComponent(country)}&count=3`, { signal: ctrl.signal });
      clearTimeout(tid);
      const data = await resp.json();
      if (data.success && data.images?.length) {
        photosRef.current = data.images;
      }
    } catch { /* silent */ }
    return photosRef.current;
  }, [tripInput, city, country]);

  const buildCard = useCallback(async (themeId, pIdx) => {
    try {
      const photos = await fetchPhotos();
      const photoUrl = photos[pIdx] || photos[0] || null;
      const dataUrl = await generateTripCard(tripInput, generatedItinerary, photoUrl, themeId);
      if (dataUrl) {
        setShareImage(dataUrl);
        setShareText(fallbackText);
      } else {
        toast.error('Failed to generate card.');
        setShareText(fallbackText);
      }
    } catch (err) {
      toast.error('Card generation failed.');
      setShareText(fallbackText);
    } finally {
      setLoading(false);
    }
  }, [tripInput, generatedItinerary, fallbackText, fetchPhotos]);

  const openRef = useRef(open);
  useEffect(() => {
    const wasOpen = openRef.current;
    openRef.current = open;
    if (open && !wasOpen) {
      setLoading(true);
      buildCard('ocean', 0);
    }
    if (!open && wasOpen) {
      setShareImage(null);
      setShareText('');
      setLoading(false);
      setActiveTheme('ocean');
      setPhotoIndex(0);
    }
  }, [open, buildCard]);

  const handleThemeChange = async (themeId) => {
    setActiveTheme(themeId);
    setLoading(true);
    await buildCard(themeId, photoIndex);
  };

  const handlePhotoPrev = async () => {
    const photos = photosRef.current || [];
    if (photos.length <= 1) return;
    const newIdx = (photoIndex - 1 + photos.length) % photos.length;
    setPhotoIndex(newIdx);
    setLoading(true);
    await buildCard(activeTheme, newIdx);
  };

  const handlePhotoNext = async () => {
    const photos = photosRef.current || [];
    if (photos.length <= 1) return;
    const newIdx = (photoIndex + 1) % photos.length;
    setPhotoIndex(newIdx);
    setLoading(true);
    await buildCard(activeTheme, newIdx);
  };

  const photoCount = photosRef.current?.length || 0;

  const downloadImage = () => {
    if (!shareImage) return;
    const link = document.createElement('a');
    link.href = shareImage;
    link.download = `${city.replace(/\s+/g, '-')}-trip-card.png`;
    link.click();
    toast.success('Image downloaded!');
  };

  const copyText = async () => {
    try { await navigator.clipboard.writeText(shareText); toast.success('Caption copied to clipboard!'); }
    catch { toast.error('Failed to copy'); }
  };

  const shareNative = async () => {
    if (!navigator.share) { toast.error('Native sharing not supported'); return; }
    try {
      const shareData = { title: `Trip to ${city}`, text: shareText };
      if (shareImage) {
        try {
          const resp = await fetch(shareImage);
          const blob = await resp.blob();
          const file = new File([blob], 'trip-card.png', { type: 'image/png' });
          if (navigator.canShare?.({ files: [file] })) shareData.files = [file];
        } catch {}
      }
      await navigator.share(shareData);
    } catch (err) { if (err.name !== 'AbortError') toast.error('Sharing failed'); }
  };

  const shareWhatsApp = () => window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`, '_blank');
  const shareTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, '_blank');
  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(shareText)}`, '_blank');
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success('Link copied!'); }
    catch { toast.error('Failed to copy link'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl bg-white/95 backdrop-blur-xl max-h-[90vh] overflow-y-auto" data-testid="social-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-ocean flex items-center gap-2">
            <Share2 className="w-5 h-5 text-gold" />
            Share Your Trip
          </DialogTitle>
          <DialogDescription>Share your {city} trip on social media</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Theme selector */}
          <ThemeSelector activeTheme={activeTheme} onSelect={handleThemeChange} />

          {/* Share Card Image with photo carousel arrows */}
          <div className="relative rounded-xl overflow-hidden border border-ocean/10 shadow-md bg-ocean/5 group" data-testid="share-card-container">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-ocean mb-3" />
                <p className="text-ocean/60 text-sm">Creating share card...</p>
              </div>
            ) : shareImage ? (
              <>
                <img src={shareImage} alt={`Trip to ${city}`} className="w-full h-auto" data-testid="share-card-image" />
                {/* Photo navigation arrows — only on the left half (photo area) */}
                {photoCount > 1 && (
                  <>
                    <button
                      onClick={handlePhotoPrev}
                      data-testid="photo-prev-btn"
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 backdrop-blur-sm"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={handlePhotoNext}
                      data-testid="photo-next-btn"
                      className="absolute top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 backdrop-blur-sm"
                      style={{ left: 'calc(50% - 44px)' }}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    {/* Dot indicators */}
                    <div className="absolute bottom-3 flex gap-1.5 justify-center" style={{ left: '0', width: '50%' }} data-testid="photo-dots">
                      {Array.from({ length: photoCount }).map((_, i) => (
                        <span
                          key={`dot-${i}`}
                          className={`w-2 h-2 rounded-full transition-all duration-200 ${
                            i === photoIndex ? 'bg-white scale-125 shadow-md' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center py-16">
                <p className="text-ocean/40">Failed to generate card</p>
              </div>
            )}
          </div>

          {/* Caption */}
          {shareText && (
            <div className="bg-ocean/5 rounded-xl p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-charcoal flex-1">{shareText}</p>
                <Button variant="ghost" size="icon" onClick={copyText} className="h-7 w-7 flex-shrink-0 text-ocean/50 hover:text-ocean" data-testid="copy-caption-btn">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}

          {/* Share Buttons */}
          <div className="grid grid-cols-2 gap-2">
            {navigator.share && (
              <Button onClick={shareNative} className="bg-ocean hover:bg-ocean-light text-white rounded-xl h-11 col-span-2" data-testid="native-share-btn">
                <Share2 className="w-4 h-4 mr-2" /> Share
              </Button>
            )}
            <Button onClick={shareWhatsApp} variant="outline" className="rounded-xl h-11 border-green-300 text-green-700 hover:bg-green-50" data-testid="whatsapp-share-btn">
              <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
            </Button>
            <Button onClick={shareTwitter} variant="outline" className="rounded-xl h-11 border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="twitter-share-btn">
              <Twitter className="w-4 h-4 mr-2" /> Twitter / X
            </Button>
            <Button onClick={shareFacebook} variant="outline" className="rounded-xl h-11 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="facebook-share-btn">
              <Facebook className="w-4 h-4 mr-2" /> Facebook
            </Button>
            <Button onClick={copyLink} variant="outline" className="rounded-xl h-11 border-ocean/20 text-ocean hover:bg-ocean/5" data-testid="copy-link-btn">
              <Link2 className="w-4 h-4 mr-2" /> Copy Link
            </Button>
            <Button onClick={() => { onOpenChange(false); onEmailShare?.(); }} variant="outline" className="rounded-xl h-11 border-amber-300 text-amber-700 hover:bg-amber-50 col-span-2" data-testid="email-share-btn">
              <Mail className="w-4 h-4 mr-2" /> Email to Friends
            </Button>
          </div>

          {/* Download */}
          {shareImage && (
            <Button onClick={downloadImage} variant="outline" className="w-full rounded-xl h-11 border-gold/30 text-gold-dark hover:bg-gold/10" data-testid="download-card-btn">
              <Download className="w-4 h-4 mr-2" /> Download Trip Card
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
